import { useNavigate } from 'react-router-dom';
import { Compass, FlaskConical } from 'lucide-react';
import { useTour } from '../tour/Tour';
import type { Role } from '../../types';
import { useApp } from '../../context/AppContext';
import { useT } from '../../i18n';
import { ROLES } from '../../config/navigation';
import { useFacilities } from '../../hooks/useAppData';
import { facilityName } from '../../lib/patient';

const selectCls =
  'h-10 rounded-xl border border-sc-line bg-white pl-2.5 pr-7 text-[13px] md:pl-3 md:text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-sc-teal';

export function DemoControls({ suffix = '' }: { suffix?: string }) {
  const { role, setRole, facilityId, setFacilityId, lang } = useApp();
  const t = useT();
  const navigate = useNavigate();
  const { data: facilities = [] } = useFacilities();
  const { start, active } = useTour();

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2 md:flex-nowrap" title={t('topbar.demoNote')}>
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-sc-saffron-soft px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-800">
        <FlaskConical className="h-3.5 w-3.5" />
        {t('demo.only')}
      </span>
      {!active && (
        <button type="button" onClick={() => start(role === 'staff' ? 'reception' : 'doctor')} data-testid={`tour-restart${suffix}`} className="inline-flex h-10 shrink-0 items-center gap-1 rounded-xl border border-sc-line bg-white px-2 text-sm font-semibold text-sc-teal" title={t('tour.restart')}>
          <Compass className="h-4 w-4" /><span className="hidden sm:inline">{t('tour.restart')}</span>
        </button>
      )}
      <div className="grid w-full grid-cols-2 gap-2 md:flex md:w-auto">
      <select
        aria-label={t('topbar.role')}
        data-testid={`role-switcher${suffix}`}
        className={`${selectCls} w-full min-w-0 md:w-auto md:flex-none`}
        value={role}
        onChange={(e) => {
          setRole(e.target.value as Role);
          navigate('/');
        }}
      >
        {ROLES.map((r) => (
          <option key={r} value={r}>
            {t(`roles.${r}`)}
          </option>
        ))}
      </select>
      {role !== 'super_admin' && (
        <select
          aria-label={t('topbar.clinic')}
          data-testid={`clinic-switcher${suffix}`}
          className={`${selectCls} w-full min-w-0 md:w-auto md:max-w-[12rem] md:flex-none`}
          value={facilityId}
          onChange={(e) => setFacilityId(e.target.value)}
        >
          {facilities.map((f) => (
            <option key={f.id} value={f.id}>
              {facilityName(f, lang)}
            </option>
          ))}
        </select>
      )}
      </div>
    </div>
  );
}
